import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from "rxjs";
import { D2XAuthService } from "../services/d2x-auth.service";
import { AuthApi } from "../apis/auth.api";
import { MatSnackBar } from "@angular/material/snack-bar";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authService: D2XAuthService,
    private authApi: AuthApi,
    private snackBar: MatSnackBar
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('--- Interceptor 작동: 요청 가로채기 ---');
    console.log('요청 URL:', req.url);

    // 1. 요청 헤더에 Access Token 추가
    const accessToken = this.authService.getAccessToken();
    const sessionId = this.authService.getSessionId(); // 세션 ID를 가져오는 메서드가 필요
    const isLoginState = this.authService.checkLoginStatus(); // 로그인 상태 확인 메서드 추가 (필요 시)
    
    // 로그인 API 요청인 경우 토큰 관련 로직을 건너뜁니다.
    if (req.url.includes('/v1/auth/login')) {
        console.log('로그인 요청이므로 인터셉터 로직을 건너뜁니다.');
        return next.handle(req);
    }
    
    // 1. 로그인 상태가 아니거나 AccessToken이 없으면 토큰 없이 요청
    if (!accessToken && !isLoginState) {
        console.warn('로그인 상태가 아닙니다. 토큰 없이 요청을 보냅니다.');
        return next.handle(req);
    }

    // 2. 요청 헤더에 Access Token 추가 (로그인 상태일 경우)
    if (accessToken) {
      req = this.addTokenAndSessionId(req, accessToken, sessionId);
      console.log('요청 헤더:', req.headers.get('Authorization'));
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && error.error.error_code === 'INVALID_ACCESS_TOKEN') {
          
          return this.refreshAccessToken(req, next);
        }
        else if (error.status === 401 && error.error.error_code === 'INVALID_SESSION')  {
          this.snackBar.open('로그인 정보가 만료되었습니다. 다시 로그인해주세요.', '확인', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          // 403: 세션 무효화 에러 -> 즉시 로그아웃
          console.warn('Session is no longer valid. Logging out.');
          this.authService.logoutAndRedirect();
          return throwError(() => error);
        }
        else {
          return throwError(() => error);
        }
      })
    );
  }

  // 요청 헤더에 토큰과 세션 ID를 추가하는 헬퍼 함수
  private addTokenAndSessionId(req: HttpRequest<any>, token: string, sessionId: string | null) {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    if (sessionId) {
      headers = headers.set('X-Session-ID', sessionId);
    }
    return req.clone({ headers });
  }

  // 401 && INVALID_ACCESS_TOKEN 에러 처리 로직
  private refreshAccessToken(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // 1. 리프레시 토큰 가져오기
      const refreshToken = this.authService.getRefreshToken();
      if (!refreshToken) {
        // 리프레시 토큰이 없으면 로그아웃 처리
        this.authService.logoutAndRedirect();
        return throwError(() => new Error('No refresh token available'));
      }

      // 2. 리프레시 토큰으로 새로운 AccessToken 요청
      return this.authApi.refreshToken(refreshToken).pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          // 3. 새로운 토큰 저장
          this.authService.setLoginData(response);
          this.refreshTokenSubject.next(response.access_token);
          // 4. 원래 요청을 새 토큰으로 재시도
          const sessionId = this.authService.getSessionId();
          return next.handle(this.addTokenAndSessionId(req, response.access_token, sessionId));
        }),
        catchError((err) => {
          // 리프레시 토큰 갱신 실패 (토큰 만료, 서버 에러 등)
          this.isRefreshing = false;
          this.authService.logoutAndRedirect();
          return throwError(() => err);
        })
      );
    } else {
      // 이미 토큰 갱신 중인 경우, 갱신이 완료될 때까지 대기
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const sessionId = this.authService.getSessionId();
          return next.handle(this.addTokenAndSessionId(req, token, sessionId));
        })
      );
    }
  }
  
}